import { AuthenticationResult, PublicClientApplication } from '@azure/msal-browser';
import { IFrameLoginPage } from '@core/pages/iFrameLoginPage/iFrameLoginPage';
import React from 'react';
import useUserStore from '@core/stores/useUserStore';
import { inIframe, refreshAfterError } from '@shared/utils';
import { isLogLevel, logger } from '@shared/logger';
import { ErrorPage } from '@core/pages/ErrorPage/ErrorPage';
import dayjs from 'dayjs';
import { TTokenData, TUserData, TUserRole } from '@shared/types';
import { getCookie, setCookie } from 'tiny-cookie';
import tenantConfig, { devGroup, adminGroup } from '../../tenantConfig';

const SCOPE_NAME = 'authService';

const envData: ImportMetaEnv = import.meta.env;

const tokenValidationInterval = Number(envData.VITE_AUTH_VALIDATION_JOB_INTERVAL ?? 60000);
const refreshTokenThreshold = Number(envData.VITE_AUTH_TOKEN_REFRESH_THRESHOLD ?? 900000) / 1000;

/**
 * AuthService class handles user authentication, token management, and session handling using MSAL.
 */
export class AuthService {
    private readonly msalInstance: PublicClientApplication;

    /**
     * Initializes the AuthService by creating an MSAL instance and setting up token validation interval.
     */
    constructor() {
        logger.log(SCOPE_NAME, 1, 'Create AuthService Singleton');
        this.msalInstance = new PublicClientApplication({
            auth: {
                clientId: envData.VITE_AUTH_CLIENT_ID, // Client ID for the application from environment variables
                authority: envData.VITE_AUTH_AUTHORITY, // Authority (tenant-specific endpoint) for authentication
                redirectUri: envData.VITE_BASE_URL, // URL where users are redirected post-login
            },
            system: {
                loggerOptions: {
                    loggerCallback(logLevel, message) {
                        logger.log('MSAL', 2, message); // Log if level 2 or 3
                    },
                    logLevel: isLogLevel(3) ? 3 : 2,
                },
            },
        });
        this.init();
    }

    /**
     * Initializes MSAL instance and starts token validation at a set interval.
     * @returns The initialized MSAL instance.
     */
    public async init() {
        await this.msalInstance.initialize();
        logger.log(SCOPE_NAME, 1, 'MSAL init');
        this.runTokenValidationInterval();
        return this.msalInstance;
    }

    /**
     * Starts a periodic interval to validate and refresh the authentication token.
     */
    public runTokenValidationInterval() {
        typeof document !== 'undefined' &&
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible') {
                    this.tokenHasExpired() && this.refreshToken();
                }
            });
        logger.log(
            SCOPE_NAME,
            1,
            'start tokenValidationWorker, Intervall is set to:',
            tokenValidationInterval,
        );
        setInterval(() => {
            this.tokenHasExpired() && this.refreshToken();
        }, tokenValidationInterval);
    }

    /**
     * Returns the MSAL instance used for authentication.
     * @returns The MSAL instance.
     */
    public getMsalInstance() {
        return this.msalInstance;
    }

    /**
     * Retrieves the active user account from MSAL.
     * @returns The active account or null if no account is available.
     */
    public getActiveAccount() {
        const accounts = this.msalInstance.getAllAccounts();
        return accounts.length > 0 ? accounts[0] : null;
    }

    /**
     * Checks if the authentication token has expired.
     * @returns True if the token is expired, false otherwise.
     */
    public tokenHasExpired() {
        const now = Math.floor(Date.now() / 1000);
        const expirationDateFromToken = this.getExpirationDateFromToken();
        if (!expirationDateFromToken) return true;

        const expiration = expirationDateFromToken - refreshTokenThreshold <= now;

        logger.log(
            SCOPE_NAME,
            3,
            'token check',
            dayjs
                .unix(expirationDateFromToken - refreshTokenThreshold)
                .format('YYYY-MM-DD HH:mm:ss') +
                ' <= ' +
                dayjs.unix(now).format('YYYY-MM-DD HH:mm:ss') +
                ' = ',
            expirationDateFromToken - refreshTokenThreshold <= now,
        );
        return expiration;
    }

    /**
     * Refreshes the token if expired or if a forced refresh is triggered.
     * @param forceRefresh - Indicates whether to force a token refresh.
     * @param firstRun - Indicates if it's the first time running the token refresh.
     * @returns A promise that resolves when the token refresh is complete.
     */
    public async refreshToken(
        forceRefresh = false,
        firstRun = false,
    ): Promise<void | React.ReactElement> {
        const activeAccount = this.getActiveAccount();
        logger.log(SCOPE_NAME, 2, 'Validate token. ForceRefresh:', forceRefresh);

        if (!activeAccount) {
            logger.warn(SCOPE_NAME, 1, 'No active account found');
            return this.login();
        }

        const shouldFetchToken = firstRun || this.tokenHasExpired() || forceRefresh;
        if (!shouldFetchToken) return;

        try {
            logger.log(SCOPE_NAME, 1, 'Fetch accessToken for:', activeAccount);
            const response: AuthenticationResult = await this.msalInstance.acquireTokenSilent({
                scopes: [envData.VITE_AUTH_SCOPE],
                account: activeAccount,
                forceRefresh: this.tokenHasExpired() || forceRefresh,
            });

            if (response.idTokenClaims && response.accessToken) {
                const tokenClaims = response.idTokenClaims as TTokenData;
                await this.writeUserDataToStore({
                    preferred_username: tokenClaims.preferred_username || '',
                    name: tokenClaims.name || '',
                    token: response.accessToken,
                    tokenData: tokenClaims,
                });
                await this.getUserAvatar();
            } else {
                logger.error(SCOPE_NAME, 1, 'accessToken fetch failed:', response);
                if (refreshAfterError()) return React.createElement(ErrorPage);
            }
        } catch (error) {
            if (!getCookie('tokenLoginFailure')) {
                setCookie('tokenLoginFailure', '1', { expires: '50s' });
                return this.login();
            } else {
                if (refreshAfterError()) return React.createElement(ErrorPage);
            }
        }
    }

    public login(): void | React.ReactElement {
        if (inIframe()) {
            // send the user with react router to the login page
            return React.createElement(IFrameLoginPage, {
                loginClick: () => this.loginPopup(),
            });
        } else {
            this.loginRedirect();
        }
    }

    public getExpirationDateFromToken() {
        if (useUserStore.getState().tokenData) {
            return useUserStore.getState().tokenData?.exp as number;
        } else {
            const activeAccount = this.msalInstance.getActiveAccount();
            return activeAccount && activeAccount.idTokenClaims
                ? activeAccount.idTokenClaims.exp
                : undefined;
        }
    }

    /**
     * Logs out the currently authenticated user by clearing the account in MSAL.
     */
    public logout() {
        const accounts = this.msalInstance.getAllAccounts();
        if (accounts.length > 0) {
            this.msalInstance.logoutPopup({ account: accounts[0] });
        } else {
            logger.warn(SCOPE_NAME, 1, 'No accounts to log out');
        }
    }

    /**
     * Initiates login via a popup.
     * @returns A promise that resolves once the user is authenticated.
     */
    public loginPopup(): void {
        this.msalInstance
            .loginPopup({ scopes: [envData.VITE_AUTH_SCOPE] })
            .then((response) => {
                if (response.account) {
                    this.writeUserDataToStore({
                        preferred_username:
                            response.account.idTokenClaims!.preferred_username || '',
                        name: response.account.idTokenClaims!.name || '',
                        token: response.accessToken,
                        tokenData: response.account.idTokenClaims as TTokenData,
                    });
                }
            })
            .catch((error) => {
                logger.log(SCOPE_NAME, 1, 'Login popup failed:', error);
            });
    }

    /**
     * Initiates login via redirect.
     */
    public loginRedirect() {
        this.msalInstance.loginRedirect({ scopes: [envData.VITE_AUTH_SCOPE] });
    }

    /**
     * Retrieves the user's avatar image URL from the Microsoft Graph API.
     * @returns {string} The URL of the user's avatar image or a default image
     */
    public async getUserAvatar(): Promise<string | undefined> {
        try {
            const user = useUserStore.getState();
            if (user.avatarUrl) {
                return user.avatarUrl;
            }

            const account = this.getActiveAccount();
            if (!account) {
                return '';
            }

            const response = await this.msalInstance.acquireTokenSilent({
                scopes: ['user.read'], // Add user.read permission
                account: account,
            });

            const accessToken = response.accessToken;

            const graphEndpoint = 'https://graph.microsoft.com/v1.0/me/photo/$value';
            const avatarResponse = await fetch(graphEndpoint, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            if (avatarResponse.ok) {
                const blob = await avatarResponse.blob();
                const imageUrl = URL.createObjectURL(blob);
                useUserStore.getState().setUserData({
                    ...user,
                    avatarUrl: imageUrl,
                });
                return imageUrl;
            } else {
                logger.warn(
                    SCOPE_NAME,
                    1,
                    'Failed to fetch user avatar from graph:',
                    avatarResponse,
                );
            }
        } catch (error) {
            logger.warn(SCOPE_NAME, 1, 'Error fetching user avatar from MS Graph:', error);
        }
    }

    /**
     * Returns the user's token data from the store.
     * @returns The user's token data.
     */
    public getTokenData() {
        return useUserStore.getState().tokenData;
    }

    public isAdmin(): boolean {
      return useUserStore.getState().tokenData?.groups?.includes(adminGroup) || false;
    }

    public hasAnyRole(roles: TUserRole[]): boolean {
      const userRoles = useUserStore.getState().tokenData?.groups;
      let roleMatched = false;
      
      roles.forEach(role => {
        if (userRoles?.includes(role.id)) {
          roleMatched = true;
        }
      });

      return roleMatched;
    }

    /**
     * Returns the user's token from the store.
     * @returns The user's token.
     */

    public getToken() {
        return useUserStore.getState().token;
    }

    /**
     * Fetches the user's group memberships from the Microsoft Graph API.
     * @returns An array of group IDs the user is a member of.
     */

    private async getUserGroupsFromMSAPI(): Promise<string[]> {
        // if the userGroups are already set, return them, from the session
        if (getCookie('userGroups')) {
            return getCookie('userGroups')?.split(',') || [];
        } else {
            // if the userGroups are not set, fetch them from the Graph API, to prevent to multiple calls to the Graph API, we set the userGroups to the cookie

            try {
                const account = this.getActiveAccount();
                if (!account) {
                    return [];
                }

                const responseToken = await this.msalInstance.acquireTokenSilent({
                    scopes: ['https://graph.microsoft.com/.default'],
                    account: account,
                });

                const accessToken = responseToken.accessToken;

                const endpoint = 'https://graph.microsoft.com/v1.0/me/memberOf?$count=true';

                // build the filter for the groups, coming from tenantConfig
                let filter = "&$filter=id in ('" + devGroup + "',"; //add the devGroup to the filter
                tenantConfig.forEach((tenant) => {
                    tenant.groups.forEach((group) => {
                        filter += `'${group}',`;
                    });
                });
                filter = filter.slice(0, -1) + ')';
                //

                const response = await fetch(endpoint + filter, {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                        ConsistencyLevel: 'eventual',
                    },
                });

                if (!response.ok) {
                    logger.error(SCOPE_NAME, 1, `Graph API request failed: ${response.status}`);
                }
                const data = await response.json();

                // Log the user groups (sentry removed)

                //if the data is empty, return an empty array
                if (!data.value) {
                    return [];
                }

                // Extract group IDs from the response
                const userGroups = data.value
                    .filter((group: { id: string }) => group.id) // Ensure the group has an ID
                    .map((group: { id: string }) => group.id);

                // log the userGroups if more than 200
                logger.log(
                    SCOPE_NAME,
                    1,
                    'Fetched user group and set it to Token, because of more than 200:',
                    userGroups,
                );

                //set the userGroup to the cookie
                setCookie('userGroups', userGroups.join(','), { expires: 1 });

                return userGroups;
            } catch (error) {
                console.error('Error fetching user groups from Graph API:', error);
                return [];
            }
        }
    }

    /**
     * Saves user data (token, token claims) to the global store.
     *
     * @param userData
     */
    private async writeUserDataToStore(userData: TUserData) {
        if (userData.token && userData.tokenData) {
            // If the user is a member of more than 200 groups, we need to fetch the groups from the Graph API
            if (!userData.tokenData.groups) {
                userData.tokenData.groups = await this.getUserGroupsFromMSAPI();
            }

            useUserStore.getState().setUserData({
                preferred_username: userData.preferred_username,
                name: userData.name,
                tokenData: userData.tokenData,
                token: userData.token,
            });
        }
    }
}

/**
 * Singleton instance of the AuthService class.
 */
export const authService = new AuthService();
